import React from "react";
import { Helmet } from "react-helmet";

const SITE_NAME = "Esmibar";
// Este subdominio (esmibar.vercel.app) no es el sitio principal.
// El sitio principal con SEO es https://www.esmibar.com
const SITE_URL = "https://esmibar.vercel.app";
const CANONICAL_MAIN = "https://www.esmibar.com/map";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
const DEFAULT_DESCRIPTION =
  "Mapa interactivo de bares en Madrid. Accede al directorio completo en www.esmibar.com";
const TWITTER_HANDLE = "@esmibar";

/**
 * SEOHead — Componente centralizado para gestión de SEO.
 *
 * Props:
 *  - title: string  (sin sufijo, se añade "| Esmibar" automáticamente)
 *  - description: string
 *  - canonical: string (ruta relativa o URL completa)
 *  - image: string (URL absoluta de la imagen OG)
 *  - type: string ('website' | 'article' | 'place')
 *  - noIndex: bool (true para páginas que no deben indexarse)
 *  - jsonLd: object | object[] (datos estructurados JSON-LD)
 *  - lang: string ('es' por defecto)
 *  - keywords: string
 */
// noIndex=true por defecto: este dominio (app.esmibar.com) no debe indexarse.
// Todo el SEO reside en https://www.esmibar.com
function SEOHead({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  noIndex = true,
  jsonLd,
  lang = "es",
  keywords,
}) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} — Encuentra tu bar perfecto`;
  // Canonical siempre apunta a www.esmibar.com, no al subdominio app.esmibar.com
  const canonicalUrl = CANONICAL_MAIN;

  const absoluteImage =
    image && image.startsWith("http") ? image : `${SITE_URL}${image}`;

  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet>
      {/* Básicos */}
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && (
        <meta
          name="robots"
          content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_ES" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* JSON-LD Structured Data */}
      {jsonLdArray.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default SEOHead;
